import React from "react"
import { useTranslation } from "react-i18next"

import { setReelRunFieldAsync } from "@/base/farmhq-api"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import { useToasts } from "@/hooks/useToasts"
import { getActiveFieldsFromStateCached } from "@/redux/farm-fields.reducer"
import { useRootSelector } from "@/redux/useRootSelector"
import { AlertCard, Box, FormControl } from "@/theme"
import { ActionButtons } from "./ActionButtons"

const AUTOMATIC_VALUE = "automatic"

interface SetReelRunFieldFormProps {
  /**
   * The run's manual field pin (NOT its resolved field); undefined/null
   * preselects Automatic
   */
  currentOverrideFieldId: number | null | undefined
  reelRunId: number
  onClose: () => void
}

/**
 * Lets the user pin a reel run to a specific field when the automatic
 * (GPS end-point) attribution is wrong or ambiguous. Choosing automatic
 * restores attribution from the run's end point.
 */
export function SetReelRunFieldForm({
  currentOverrideFieldId,
  reelRunId,
  onClose,
}: SetReelRunFieldFormProps): React.JSX.Element {
  const { t } = useTranslation("statusMap")

  const fields = useRootSelector(getActiveFieldsFromStateCached)

  const [selectedValue, setSelectedValue] = React.useState<string>(
    typeof currentOverrideFieldId === "number"
      ? `${currentOverrideFieldId}`
      : AUTOMATIC_VALUE,
  )

  const toasts = useToasts()
  const { dispatch, handleError, isLoading } =
    useRequestHelpers("SetReelRunField")

  const onSubmit = () => {
    dispatch(
      setReelRunFieldAsync({
        fieldId:
          selectedValue === AUTOMATIC_VALUE ? null : Number(selectedValue),
        reelRunId,
      }),
    )
      .unwrap()
      .then(() => onClose())
      .then(() => toasts.success())
      .catch((error) =>
        handleError(error, {
          toastMessage: "default",
        }),
      )
  }

  const labelText = t("setRunField.linkTo")

  return (
    <React.Fragment>
      <AlertCard bodyText={t("setRunField.instructions")} severity={"info"} />
      <Box my={"$3"}>
        <FormControl.Provider>
          <FormControl.Label>{labelText}</FormControl.Label>
          <FormControl.Select
            isDisabled={isLoading}
            label={labelText}
            selectedValue={selectedValue}
            options={[
              {
                label: t("setRunField.automaticOption"),
                value: AUTOMATIC_VALUE,
              },
              ...fields.map((field) => ({
                label: field.fieldName,
                value: `${field.id}`,
              })),
            ]}
            onValueChange={setSelectedValue}
          />
        </FormControl.Provider>
      </Box>
      <ActionButtons
        isLoading={isLoading}
        orientation={"horizontal"}
        onPressCancel={onClose}
        onPressSubmit={onSubmit}
      />
    </React.Fragment>
  )
}
